import { get, isEmpty } from 'lodash';
import { getReferralCode } from './localStorage';
import { getRHSourceCampaign } from './sessionStorage';
import { getEnvironment } from './feature_flags';

export const CURRENT_CAMPAIGNS = {
  staging: {
    'athletic-trainer': 'MF3fd783141f',
    'event-operator': 'MFc6b1193586',
  },
  production: {
    'athletic-trainer': 'MF8982b8b773',
    'event-operator': 'MF41de858d1a',
  },
};

/**
 * Get ReferralHero campaigns for the current environment
 * @returns {object} An object with RH campaigns Ids based on the environment (e.g., { event-operator: 'MFc6b1193586', athletic-trainer: 'MF3fd783141f' })
 */
export const getActiveEnvironmentCampaigns = () => {
  const env = getEnvironment() === 'production' ? 'production' : 'staging';

  return CURRENT_CAMPAIGNS[env];
};

/**
 * Find the current active ReferralHero campaign
 * Prioritizes campaigns that have a referrer set
 *
 * @param {string[]} campaigns - Array of campaign keys
 * @returns {string|undefined} The current campaign key or undefined
 */
export const RHCurrentCampaign = campaigns => {
  return campaigns.find(campaignId => {
    const campaign = get(window, `RH_${campaignId}`, {});
    return !isEmpty(campaign.referrer);
  });
};

export const RHCurrentCampaignByMwr = (campaigns, mwrCode) => {
  return campaigns.find(campaignId => {
    const campaign = get(window, `RH_${campaignId}`, {});
    return !isEmpty(campaign.referrer) && campaign.referrer === mwrCode;
  });
};

/**
 * Find the ReferralHero campaign that matches a specific referral code
 *
 * @param {string} mwrCode - The mwr/referral code to match
 * @returns {Object|null} The matching campaign object or null
 */
export const findCampaignByMwr = mwrCode => {
  if (!mwrCode) return null;

  const source = getRHSourceCampaign();
  const campaigns = getActiveEnvironmentCampaigns();

  const campaign = campaigns[source];
  const referralCampaign = get(window, `RH_${campaign}`, null);

  if (campaign && referralCampaign && referralCampaign.referrer === mwrCode) {
    return referralCampaign;
  }

  // fallback if the source was corrupted — resolve ID → object so caller always gets the same shape
  const fallbackCampaignId = RHCurrentCampaignByMwr(Object.values(campaigns), mwrCode);

  return fallbackCampaignId ? get(window, `RH_${fallbackCampaignId}`, null) : null;
};

/**
 * Get the referral code with fallback strategy:
 * 1. Try mwr param from URL
 * 2. Try localStorage
 * 3. Try get code from window object
 *
 * @returns {string|null} The referral code or null
 */
export const getReferralCodeWithFallback = (location = {}) => {
  // First, try get mwr params from url (most reliable)
  if (!isEmpty(location)) {
    const params = new URLSearchParams(get(location, 'search', ''));

    if (params.get('mwr')) {
      return params.get('mwr');
    }
  }

  // Second, try localStorage
  const storedCode = getReferralCode();
  if (storedCode) {
    return storedCode;
  }

  // Fallback: Try to get from ReferralHero window object
  const campaigns = getActiveEnvironmentCampaigns();
  const currentCampaign = RHCurrentCampaign(Object.values(campaigns));

  if (currentCampaign) {
    const RHCampaign = get(window, `RH_${currentCampaign}`, null);
    return RHCampaign ? get(RHCampaign, 'referrer', null) : null;
  }

  return null;
};
